/**
 * EditIntegrationModal - Edit an existing integration's configuration
 */

import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { X, ChevronDown, ChevronUp, Settings } from 'lucide-react'
import { Button } from '@/components/base/buttons'
import type { Integration, Language } from '@/types/integrations'
import { integrationDetails } from '@/lib/integrations/integrationDetails'
import { getLocalizedText, cleanIntegrationConfig } from '@/lib/integrations/integrationUtils'

interface EditIntegrationModalProps {
  open: boolean
  onClose: () => void
  onSave: (integration: Integration) => Promise<boolean>
  integration: Integration | null
  language: Language
  isValidating?: boolean
  isAdmin?: boolean
}

export function EditIntegrationModal({
  open,
  onClose,
  onSave,
  integration,
  language,
  isValidating,
  isAdmin,
}: EditIntegrationModalProps) {
  const { t } = useTranslation()
  const [integrationConfig, setIntegrationConfig] = useState<Record<string, any>>({})
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false)

  // Load integration config when modal opens
  useEffect(() => {
    if (integration) {
      const details = integrationDetails[integration.name]
      const config = { ...(integration.config || {}) }

      // Normalize number fields
      if (details?.configFields) {
        details.configFields.forEach((field) => {
          if (field.type === 'number' && config[field.key] !== undefined) {
            const value = config[field.key]
            if (typeof value === 'string' && !isNaN(Number(value)) && value !== '') {
              config[field.key] = Number(value)
            }
          }
        })
      }

      setIntegrationConfig(config)
    }
  }, [integration])

  if (!open || !integration) return null

  const details = integrationDetails[integration.name]
  if (!details) return null

  const displayName = getLocalizedText(details.displayName, language)

  // Get visible fields
  let fields = details.configFields
  if (integration.name === 'formitable') {
    fields = fields.filter((f) => !f.key.startsWith('auth.'))
  }
  if (!isAdmin) {
    fields = fields.filter((f) => !f.adminOnly)
  }
  const hasEnableFindBookings = fields.some((f) => f.key === 'enable_find_bookings')
  const hasLateArrivalEnabled = fields.some((f) => f.key === 'late_arrival_enabled')
  fields = fields.filter(
    (f) =>
      f.key !== 'cross_sell_time_gap_minutes' &&
      (!hasEnableFindBookings || f.key !== 'late_arrival_enabled') &&
      (!hasLateArrivalEnabled || f.key !== 'allowed_delay_minutes'),
  )

  const requiredFields = fields.filter((f) => f.required)
  const optionalFields = fields.filter((f) => !f.required)

  const handleSave = async () => {
    let cleanedConfig = cleanIntegrationConfig(integrationConfig)

    // Formitable special handling
    if (integration.name === 'formitable') {
      if (!cleanedConfig.cross_sell_assistant_id) {
        delete cleanedConfig.cross_sell_time_gap_minutes
      } else if (cleanedConfig.cross_sell_time_gap_minutes === undefined) {
        cleanedConfig.cross_sell_time_gap_minutes = 120
      }

      // Preserve auth email
      if (integration.config?.auth?.email) {
        cleanedConfig = {
          ...cleanedConfig,
          auth: {
            ...cleanedConfig.auth,
            email: integration.config.auth.email,
          },
        }
      }
    }

    const success = await onSave({
      ...integration,
      config: cleanedConfig,
    })
    if (success) {
      onClose()
    }
  }

  const renderField = (field: any) => {
    const label = getLocalizedText(field.label, language)
    const description = getLocalizedText(field.description, language)

    if (field.type === 'radio') {
      return (
        <div key={field.key} className="space-y-2">
          <label className="text-sm font-semibold text-gray-900 dark:text-gray-100">{label}</label>
          <p className="text-xs text-gray-500 dark:text-gray-400">{description}</p>
          <div className="mt-2 space-y-2">
            {field.options?.map((option: any) => (
              <label key={option.value} className="flex cursor-pointer items-center gap-2">
                <input
                  type="radio"
                  name={field.key}
                  value={option.value}
                  checked={(integrationConfig[field.key] ?? field.defaultValue) === option.value}
                  onChange={(e) => {
                    const shouldConvertToNumber = typeof option.value === 'number'
                    const newValue = shouldConvertToNumber ? Number(e.target.value) : e.target.value
                    setIntegrationConfig((prev) => ({ ...prev, [field.key]: newValue }))
                  }}
                  className="h-4 w-4 border-gray-300 text-primary focus:ring-primary"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {getLocalizedText(option.label, language)}
                </span>
              </label>
            ))}
          </div>
        </div>
      )
    }

    if (field.type === 'select') {
      return (
        <div key={field.key} className="space-y-2">
          <label className="text-sm font-semibold text-gray-900 dark:text-gray-100">{label}</label>
          <p className="text-xs text-gray-500 dark:text-gray-400">{description}</p>
          <select
            value={integrationConfig[field.key] ?? field.defaultValue ?? ''}
            onChange={(e) => {
              const firstOption = field.options?.[0]
              const shouldConvertToNumber = firstOption && typeof firstOption.value === 'number'
              const newValue = shouldConvertToNumber ? Number(e.target.value) : e.target.value
              setIntegrationConfig((prev) => ({ ...prev, [field.key]: newValue }))
            }}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          >
            {field.options?.map((option: any) => (
              <option key={option.value} value={option.value}>
                {getLocalizedText(option.label, language)}
              </option>
            ))}
          </select>
        </div>
      )
    }

    if (field.type === 'boolean') {
      const isFindBookings = field.key === 'enable_find_bookings'
      const isFindBookingsEnabled =
        integrationConfig['enable_find_bookings'] ?? true
      const lateArrivalField = isFindBookings
        ? details.configFields.find((f) => f.key === 'late_arrival_enabled')
        : null

      const isLateArrival = field.key === 'late_arrival_enabled'
      const isLateArrivalEnabled =
        integrationConfig['late_arrival_enabled'] ?? false
      const delayField = isLateArrival
        ? details.configFields.find((f) => f.key === 'allowed_delay_minutes')
        : null

      return (
        <div key={field.key} className="space-y-2">
          <label className="text-sm font-semibold text-gray-900 dark:text-gray-100">{label}</label>
          <p className="text-xs text-gray-500 dark:text-gray-400">{description}</p>
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={integrationConfig[field.key] ?? field.defaultValue ?? false}
              onChange={(e) =>
                setIntegrationConfig((prev) => ({ ...prev, [field.key]: e.target.checked }))
              }
              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
          </label>
          {isFindBookings && isFindBookingsEnabled && lateArrivalField && (
            <div className="mt-4">{renderField(lateArrivalField)}</div>
          )}
          {isLateArrival && isLateArrivalEnabled && delayField && (
            <div className="mt-4">{renderField(delayField)}</div>
          )}
        </div>
      )
    }

    // Text/password/number
    const isCrossSellAssistant = field.key === 'cross_sell_assistant_id'
    const hasAssistantId =
      integrationConfig['cross_sell_assistant_id'] !== '' &&
      integrationConfig['cross_sell_assistant_id'] !== undefined
    const timeGapField = isCrossSellAssistant
      ? details.configFields.find((f) => f.key === 'cross_sell_time_gap_minutes')
      : null

    return (
      <div key={field.key} className="space-y-2">
        <label className="text-sm font-semibold text-gray-900 dark:text-gray-100">{label}</label>
        <p className="text-xs text-gray-500 dark:text-gray-400">{description}</p>
        <input
          type={field.type}
          value={integrationConfig[field.key] ?? field.defaultValue ?? ''}
          onChange={(e) => {
            const rawValue = e.target.value
            const newValue =
              field.type === 'number' ? (rawValue === '' ? '' : Number(rawValue)) : rawValue
            setIntegrationConfig((prev) => ({ ...prev, [field.key]: newValue }))
          }}
          required={field.required}
          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
        />
        {isCrossSellAssistant && hasAssistantId && timeGapField && (
          <div className="mt-3 border-l-2 border-gray-200 pl-4 dark:border-gray-700">
            {renderField(timeGapField)}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-900">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <img
              src={details.iconPath}
              alt={displayName}
              className="h-8 w-8"
            />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {t('resources.assistants.integrations.edit_title', { name: displayName })}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto px-6 py-4">
          <div className="space-y-6">
            {/* Required fields */}
            {requiredFields.length > 0 && (
              <div>
                <h3 className="mb-4 text-base font-semibold text-gray-900 dark:text-gray-100">
                  {t('resources.assistants.integrations.required_settings')}
                </h3>
                <div className="max-w-lg space-y-4">{requiredFields.map(renderField)}</div>
              </div>
            )}

            {/* Optional fields */}
            {optionalFields.length > 0 && (
              <div>
                <button
                  type="button"
                  onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
                  className="flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-primary"
                >
                  <Settings className="h-4 w-4" />
                  {t('resources.assistants.integrations.advanced_settings')}
                  <span className="rounded-full border border-gray-300 px-2 py-0.5 text-xs dark:border-gray-600">
                    {t('resources.assistants.integrations.optional_count', { count: optionalFields.length })}
                  </span>
                  {showAdvancedSettings ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </button>

                {showAdvancedSettings && (
                  <div className="mt-4 max-w-lg space-y-4 border-l-2 border-gray-200 pl-4 dark:border-gray-700">
                    <p className="text-xs italic text-gray-500 dark:text-gray-400">
                      {t('resources.assistants.integrations.optional_hint')}
                    </p>
                    {optionalFields.map(renderField)}
                  </div>
                )}
              </div>
            )}

            {requiredFields.length === 0 && optionalFields.length === 0 && (
              <div className="py-8 text-center">
                <p className="text-gray-500 dark:text-gray-400">
                  {t('resources.assistants.integrations.no_config')}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end border-t border-gray-200 px-6 py-4 dark:border-gray-700">
          <Button type="button" onPress={handleSave} isDisabled={isValidating} isLoading={isValidating}>
            {t('resources.assistants.integrations.save_changes')}
          </Button>
        </div>
      </div>
    </div>
  )
}
